<template>
  <MainLayout>
    <ConfirmDialog />
    <Toast />
    <template #header>
      <div class="mb-6">
        <router-link to="/organisations" class="text-blue-600 hover:underline mb-2 inline-block">
          ← Back to Organisations
        </router-link>
        <h1 class="text-2xl font-bold">{{ organisation?.name || 'Loading...' }}</h1>
        <p v-if="organisation" class="text-gray-600">
          Subdomain:
          <a
            v-if="organisation.subdomain || organisation.custom_login_subdomain"
            :href="`https://${organisation.subdomain || organisation.custom_login_subdomain}.alldoq.com`"
            target="_blank"
            rel="noopener noreferrer"
            class="font-mono text-blue-600 hover:underline inline-flex items-center"
          >
            {{ organisation.subdomain || organisation.custom_login_subdomain }}
            <i class="pi pi-external-link ml-1 text-xs"></i>
          </a>
          <span v-else class="font-mono">—</span>
        </p>
      </div>
    </template>

    <div v-if="loading" class="text-center py-8">
      <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
      <p class="mt-2">Loading organisation...</p>
    </div>

    <div v-else-if="organisation">
      <TabView v-model:activeIndex="activeTab">
        <!-- Users Tab -->
        <TabPanel header="Users">
          <div class="flex flex-wrap items-center gap-3 mb-4 pl-6">
            <Button
              label="Add User"
              icon="fa-solid fa-user-plus"
              class="bg-blue-900 hover:bg-blue-800 text-white border-blue-900 py-3 px-6"
              @click="openAddPersonDialog"
            />
            <span class="p-input-icon-left flex-1 min-w-[260px] max-w-md">
              <i class="pi pi-search" />
              <InputText
                v-model="peopleSearch"
                placeholder="Search users + associates by name or email"
                class="w-full"
              />
            </span>
            <span class="text-sm text-gray-500">
              {{ filteredUsers.length }} / {{ users.length }}
            </span>
          </div>

          <DataTable
            :value="filteredUsers"
            :paginator="true"
            :rows="100"
            :rowsPerPageOptions="[25, 50, 100, 200]"
            dataKey="id"
            responsiveLayout="scroll"
            class="p-datatable-users"
            :loading="usersLoading"
          >
            <template #empty>
              <div class="text-center py-4">No users found</div>
            </template>
            <template #loading>
              <div class="text-center py-4">Loading users...</div>
            </template>

            <Column field="name" header="Name" :sortable="true"></Column>
            <Column field="email" header="Email" :sortable="true"></Column>
            <Column field="role" header="Role" :sortable="true" bodyClass="whitespace-nowrap">
              <template #body="slotProps">
                <Tag class="whitespace-nowrap" :value="getUserRoleLabel(slotProps.data)" :severity="getUserRoleSeverity(slotProps.data)" />
              </template>
            </Column>
            <Column field="active" header="Status" :sortable="true" bodyClass="whitespace-nowrap">
              <template #body="slotProps">
                <Tag class="whitespace-nowrap"
                     :value="slotProps.data.active ? 'Active' : 'Inactive'"
                     :severity="slotProps.data.active ? 'success' : 'danger'" />
              </template>
            </Column>
            <Column field="invitation_status" header="Invitation" :sortable="true" bodyClass="whitespace-nowrap">
              <template #body="slotProps">
                <Tag
                  class="whitespace-nowrap"
                  :value="formatInvitationStatus(slotProps.data.invitation_status)"
                  :severity="invitationSeverity(slotProps.data.invitation_status)"
                />
              </template>
            </Column>
            <Column field="last_sign_in_at" header="Last Sign In" :sortable="true" bodyClass="whitespace-nowrap">
              <template #body="slotProps">
                <span class="whitespace-nowrap">{{ formatDate(slotProps.data.last_sign_in_at) }}</span>
              </template>
            </Column>
            <Column header="Actions" :style="{ width: '5rem' }">
              <template #body="slotProps">
                <Menu ref="userMenu" :model="userMenuItems" :popup="true" />
                <Button
                  icon="pi pi-ellipsis-h"
                  class="p-button-rounded p-button-text p-button-plain"
                  @click="(event) => toggleUserMenu(event, slotProps.data)"
                  aria-label="Options"
                />
              </template>
            </Column>
          </DataTable>
        </TabPanel>

        <!-- Associates Tab -->
        <TabPanel header="Associates" @click="loadOrgAssociates">
          <div class="flex flex-wrap items-center gap-3 mb-4 pl-6">
            <Button
              label="Add Associate"
              icon="fa-solid fa-user-plus"
              class="bg-blue-900 hover:bg-blue-800 text-white border-blue-900 py-3 px-6"
              @click="openAddAssociateDialog"
            />
            <span class="p-input-icon-left flex-1 min-w-[260px] max-w-md">
              <i class="pi pi-search" />
              <InputText
                v-model="peopleSearch"
                placeholder="Search users + associates by name or email"
                class="w-full"
              />
            </span>
            <span class="text-sm text-gray-500">
              {{ filteredAssociates.length }} / {{ associates.length }}
            </span>
          </div>

          <DataTable
            :value="filteredAssociates"
            :paginator="true"
            :rows="100"
            :rowsPerPageOptions="[25, 50, 100, 200]"
            dataKey="id"
            responsiveLayout="scroll"
            :loading="associatesLoading"
          >
            <template #empty>
              <div class="text-center py-4">No associates found</div>
            </template>
            <Column field="name" header="Name" :sortable="true" />
            <Column field="email" header="Email" :sortable="true" />
            <Column field="discipline" header="Discipline" :sortable="true" />
            <Column field="active" header="Status" :sortable="true" bodyClass="whitespace-nowrap">
              <template #body="slotProps">
                <Tag class="whitespace-nowrap"
                     :value="slotProps.data.active ? 'Active' : 'Inactive'"
                     :severity="slotProps.data.active ? 'success' : 'danger'" />
              </template>
            </Column>
            <Column field="invitation_status" header="Invitation" :sortable="true" bodyClass="whitespace-nowrap">
              <template #body="slotProps">
                <Tag class="whitespace-nowrap"
                     :value="formatInvitationStatus(slotProps.data.invitation_status)"
                     :severity="invitationSeverity(slotProps.data.invitation_status)" />
              </template>
            </Column>
            <Column field="last_sign_in_at" header="Last Sign In" :sortable="true" bodyClass="whitespace-nowrap">
              <template #body="slotProps">
                <span class="whitespace-nowrap">{{ formatDate(slotProps.data.last_sign_in_at) }}</span>
              </template>
            </Column>
          </DataTable>
        </TabPanel>

        <!-- Cases Tab -->
        <TabPanel header="Cases" @click="loadCasesStats">
          <div class="flex justify-between items-center mb-4 px-2">
            <div>
              <h3 class="text-lg font-semibold">Case statistics</h3>
              <p class="text-sm text-gray-600">Total + monthly breakdown of cases for this environment.</p>
            </div>
            <Button
              label="Download CSV"
              icon="pi pi-download"
              class="bg-blue-900 hover:bg-blue-800 text-white border-blue-900 py-3 px-6"
              @click="downloadCasesCsv"
              :loading="downloadingCsv"
            />
          </div>

          <div v-if="casesStatsLoading" class="text-center py-6">
            <i class="pi pi-spin pi-spinner" style="font-size: 1.5rem"></i>
          </div>

          <div v-else-if="casesStats" class="px-2">
            <div class="grid grid-cols-2 gap-4 mb-6">
              <div class="bg-gray-50 rounded p-4">
                <div class="text-sm text-gray-600">Total cases</div>
                <div class="text-2xl font-bold">{{ casesStats.total?.count ?? 0 }}</div>
              </div>
              <div class="bg-gray-50 rounded p-4">
                <div class="text-sm text-gray-600">Total data size</div>
                <div class="text-2xl font-bold">{{ formatBytes(casesStats.total?.bytes ?? 0) }}</div>
              </div>
            </div>

            <DataTable :value="casesStats.by_month || []" responsiveLayout="scroll">
              <template #empty>
                <div class="text-center py-4">No case activity yet</div>
              </template>
              <Column field="month" header="Month" :sortable="true" />
              <Column field="count" header="Cases" :sortable="true" />
              <Column header="Data size" :sortable="true">
                <template #body="slotProps">
                  {{ formatBytes(slotProps.data.bytes) }}
                </template>
              </Column>
            </DataTable>
          </div>

          <div v-else class="text-center py-6 text-gray-500">
            No case statistics available.
          </div>
        </TabPanel>

        <!-- Feature Flags Tab -->
        <TabPanel header="Feature flags">
          <div class="p-4">
            <h3 class="text-lg font-semibold mb-2">Feature flags</h3>
            <p class="text-sm text-gray-600 mb-4">
              Toggle Worklist + product features for this environment.
              Disabled features render greyed out in the destination UI.
            </p>

            <div v-if="orgFlagsLoading" class="text-center py-6">
              <i class="pi pi-spin pi-spinner" style="font-size: 1.5rem"></i>
            </div>

            <div v-else>
              <div
                v-for="group in featureFlagGroups"
                :key="group.name"
                class="mb-6"
              >
                <h4 class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  {{ group.name }}
                </h4>
                <div
                  v-for="flag in group.flags"
                  :key="flag.key"
                  class="flex items-center justify-between border rounded p-3 mb-2"
                >
                  <div class="pr-4">
                    <div class="font-medium text-gray-700">{{ flag.label }}</div>
                    <div v-if="flag.description" class="text-sm text-gray-500 mt-0.5 max-w-2xl">
                      {{ flag.description }}
                    </div>
                    <div class="text-xs text-gray-400 font-mono mt-1">{{ flag.key }}</div>
                  </div>
                  <InputSwitch
                    :modelValue="getOrgFlag(flag)"
                    @update:modelValue="(val) => updateOrgFlag(flag, val)"
                    :loading="savingFlag === flag.key"
                    class="shrink-0"
                  />
                </div>
              </div>
            </div>
          </div>
        </TabPanel>

        <!-- Settings Tab -->
        <TabPanel header="Settings">
          <div class="p-4">
            <h3 class="text-lg font-semibold mb-4">Organisation Settings</h3>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="font-semibold">Name:</label>
                <p>{{ organisation.name }}</p>
              </div>
              <div>
                <label class="font-semibold">Subdomain:</label>
                <p class="font-mono">
                  <a
                    v-if="organisation.subdomain || organisation.custom_login_subdomain"
                    :href="`https://${organisation.subdomain || organisation.custom_login_subdomain}.alldoq.com`"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-blue-600 hover:underline inline-flex items-center"
                  >
                    {{ organisation.subdomain || organisation.custom_login_subdomain }}
                    <i class="pi pi-external-link ml-1 text-xs"></i>
                  </a>
                  <span v-else>—</span>
                </p>
              </div>
              <div>
                <label class="font-semibold">Brand with Logo:</label>
                <p>{{ organisation.brand_with_logo ? 'Yes' : 'No' }}</p>
              </div>
              <div>
                <label class="font-semibold">Created:</label>
                <p>{{ formatDate(organisation.inserted_at) }}</p>
              </div>
            </div>

            <div v-if="organisation.settings" class="mt-6">
              <h4 class="font-semibold mb-2">Additional Settings</h4>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="font-semibold">Allow External Invites:</label>
                  <p>{{ organisation.settings.allow_external_invites ? 'Yes' : 'No' }}</p>
                </div>
                <div>
                  <label class="font-semibold">Archive Days:</label>
                  <p>{{ organisation.settings.archive_days || 0 }}</p>
                </div>
              </div>
            </div>
          </div>
        </TabPanel>
      </TabView>
    </div>

    <!-- Add Person Dialog (User or Associate) -->
    <Dialog
      v-model:visible="showAddUserDialog"
      modal
      :header="newPerson.type === 'expert' ? 'Add Associate' : 'Add User'"
      :style="{ width: '600px' }"
      :closable="true"
      :dismissableMask="false"
      class="add-user-dialog"
    >
      <div class="p-fluid p-4 space-y-4">
        <!-- Person type toggle -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Person type <span class="text-red-500">*</span>
          </label>
          <div class="flex gap-3">
            <button
              type="button"
              @click="newPerson.type = 'system_user'"
              :class="[
                'flex-1 px-4 py-3 border-2 rounded-lg text-sm font-medium transition-all',
                newPerson.type === 'system_user'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              ]"
            >
              <i class="pi pi-user mr-2" />
              User
            </button>
            <button
              type="button"
              @click="newPerson.type = 'expert'"
              :class="[
                'flex-1 px-4 py-3 border-2 rounded-lg text-sm font-medium transition-all',
                newPerson.type === 'expert'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              ]"
            >
              <i class="pi pi-graduation-cap mr-2" />
              Associate
            </button>
          </div>
        </div>

        <div class="field">
          <label for="person-name" class="block mb-2 font-medium text-gray-700">
            Name <span class="text-red-500">*</span>
          </label>
          <InputText
            id="person-name"
            v-model="newPerson.name"
            class="w-full"
            :placeholder="newPerson.type === 'expert' ? 'Enter associate name' : 'Enter user name'"
          />
        </div>

        <div class="field">
          <label for="person-email" class="block mb-2 font-medium text-gray-700">
            Email <span class="text-red-500">*</span>
          </label>
          <InputText
            id="person-email"
            v-model="newPerson.email"
            type="email"
            class="w-full"
            placeholder="email@example.com"
          />
        </div>

        <div v-if="newPerson.type === 'expert'" class="field">
          <label for="person-discipline" class="block mb-2 font-medium text-gray-700">
            Discipline <span class="text-red-500">*</span>
          </label>
          <InputText
            id="person-discipline"
            v-model="newPerson.discipline"
            class="w-full"
            placeholder="e.g., Cardiology, Neurology, Radiology"
          />
        </div>

        <div v-if="newPerson.type === 'system_user'" class="field">
          <label for="person-role" class="block mb-2 font-medium text-gray-700">
            Role <span class="text-red-500">*</span>
          </label>
          <Dropdown
            id="person-role"
            v-model="newPerson.role"
            :options="personRoleOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Select a role"
            class="w-full"
          />
          <p class="mt-1 text-xs text-gray-500">{{ getRoleDescription(newPerson.role) }}</p>
        </div>

        <div v-if="newPerson.type === 'system_user'" class="flex items-start">
          <input
            id="person-externally-visible"
            v-model="newPerson.externally_visible"
            type="checkbox"
            class="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label for="person-externally-visible" class="ml-2 block text-sm text-gray-700">
            <span class="font-medium">Make user visible to other organisations</span>
            <p class="text-xs text-gray-500 mt-1">
              Enables external sharing and collaboration with accounts outside this organisation.
            </p>
          </label>
        </div>

        <p class="text-xs text-gray-500">
          A secure password is generated server-side and emailed with the invitation.
        </p>
      </div>
      <template #footer>
        <div class="flex justify-end gap-3 p-3">
          <Button
            label="Cancel"
            icon="pi pi-times"
            class="p-button-text py-3 px-6"
            @click="showAddUserDialog = false"
          />
          <Button
            :label="newPerson.type === 'expert' ? 'Invite Associate' : 'Create User'"
            icon="pi pi-check"
            class="bg-blue-900 hover:bg-blue-800 text-white border-blue-900 py-3 px-6"
            @click="createPerson"
            :loading="creatingUser"
          />
        </div>
      </template>
    </Dialog>

    <!-- Edit User Dialog -->
    <Dialog
      v-model:visible="showEditUserDialog"
      modal
      header="Edit User"
      :style="{ width: '500px' }"
      :closable="true"
      :dismissableMask="false"
      class="edit-user-dialog"
    >
      <div class="p-fluid p-4">
        <div class="field mb-4">
          <label for="edit-user-name" class="block mb-2 font-medium text-gray-700">Name</label>
          <InputText id="edit-user-name" v-model="editingUser.name" class="w-full" placeholder="John Doe" />
        </div>
        <div class="field mb-4">
          <label for="edit-user-email" class="block mb-2 font-medium text-gray-700">Email</label>
          <InputText id="edit-user-email" v-model="editingUser.email" type="email" class="w-full" placeholder="user@example.com" />
        </div>
        <div class="field mb-4">
          <label for="edit-user-role" class="block mb-2 font-medium text-gray-700">Role</label>
          <Dropdown
            id="edit-user-role"
            v-model="editingUser.selectedRole"
            :options="exchangeRoles"
            optionLabel="label"
            placeholder="Select a role"
            class="w-full"
            @change="onEditUserRoleChange"
          />
        </div>
        <div class="field mb-4">
          <label for="edit-user-2fa" class="block mb-2 font-medium text-gray-700">Two-factor authentication</label>
          <Dropdown
            id="edit-user-2fa"
            v-model="editingUser.two_factor_method"
            :options="twoFactorMethods"
            optionLabel="label"
            optionValue="value"
            placeholder="Select a 2FA method"
            class="w-full"
          />
        </div>
        <div v-if="editingUser.two_factor_method === 'sms'" class="field mb-4">
          <label for="edit-user-mobile" class="block mb-2 font-medium text-gray-700">Mobile number</label>
          <InputText
            id="edit-user-mobile"
            v-model="editingUser.mobile_number"
            class="w-full"
            placeholder="+447777777777"
          />
          <small class="block mt-1 text-gray-500">International format, starting with + (required for SMS codes).</small>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end gap-3 p-3">
          <Button
            label="Cancel"
            icon="pi pi-times"
            class="p-button-text py-3 px-6"
            @click="showEditUserDialog = false"
          />
          <Button
            label="Update User"
            icon="pi pi-check"
            class="bg-blue-900 hover:bg-blue-800 text-white border-blue-900 py-3 px-6"
            @click="updateUser"
            :loading="updatingUser"
          />
        </div>
      </template>
    </Dialog>

    <!-- Set Custom Password Dialog -->
    <Dialog
      v-model:visible="showSetPasswordDialog"
      modal
      header="Set Custom Password"
      :style="{ width: '500px' }"
      :closable="true"
      :dismissableMask="false"
    >
      <div class="p-fluid p-4">
        <p class="mb-3 text-sm text-gray-700">
          Setting a password for
          <span class="font-semibold">{{ passwordTargetUser?.email }}</span>.
          The user will be able to sign in immediately with this password.
        </p>
        <div class="field mb-4">
          <label class="block mb-2 font-medium text-gray-700">New password</label>
          <div class="flex gap-2">
            <InputText v-model="customPassword" type="text" class="w-full" autocomplete="new-password" />
            <Button icon="pi pi-copy" class="p-button-text" @click="copyPasswordToClipboard" aria-label="Copy" />
            <Button icon="pi pi-refresh" class="p-button-text" @click="customPassword = generateSuggestedPassword(); customPasswordConfirm = customPassword" aria-label="Regenerate" />
          </div>
          <small class="text-gray-500">Min 8 characters. A suggestion is pre-filled.</small>
        </div>
        <div class="field mb-4">
          <label class="block mb-2 font-medium text-gray-700">Confirm password</label>
          <InputText v-model="customPasswordConfirm" type="text" class="w-full" autocomplete="new-password" />
        </div>
        <div class="field flex items-center gap-2">
          <input id="force-confirm" type="checkbox" v-model="forceConfirmOnPasswordSet" />
          <label for="force-confirm" class="text-sm text-gray-700">
            Mark invitation as accepted (skip pending state)
          </label>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end gap-3 p-3">
          <Button label="Cancel" icon="pi pi-times" class="p-button-text py-3 px-6"
                  @click="showSetPasswordDialog = false" />
          <Button label="Set Password" icon="pi pi-check"
                  class="bg-blue-900 hover:bg-blue-800 text-white border-blue-900 py-3 px-6"
                  @click="submitSetPassword" :loading="settingPassword" />
        </div>
      </template>
    </Dialog>

  </MainLayout>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import { useAuthStore } from '../store/auth';
import MainLayout from '../components/MainLayout.vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import Tag from 'primevue/tag';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import Menu from 'primevue/menu';
import Dropdown from 'primevue/dropdown';
import InputSwitch from 'primevue/inputswitch';
import ConfirmDialog from 'primevue/confirmdialog';
import Toast from 'primevue/toast';
import axios from 'axios';

const route = useRoute();
const router = useRouter();
const toast = useToast();
const confirm = useConfirm();
const authStore = useAuthStore();

// Data
const organisation = ref(null);
const users = ref([]);
const associates = ref([]);
const loading = ref(true);
const usersLoading = ref(false);
const associatesLoading = ref(false);
const activeTab = ref(0);

// Shared search across users + associates tabs
const peopleSearch = ref('');

const matchesPeopleSearch = (row) => {
  const term = peopleSearch.value.trim().toLowerCase();
  if (!term) return true;
  const name = (row.name || '').toLowerCase();
  const email = (row.email || '').toLowerCase();
  const discipline = (row.discipline || '').toLowerCase();
  return name.includes(term) || email.includes(term) || discipline.includes(term);
};

const filteredUsers = computed(() => users.value.filter(matchesPeopleSearch));
const filteredAssociates = computed(() => associates.value.filter(matchesPeopleSearch));

// Dialog states
const showAddUserDialog = ref(false);
const showEditUserDialog = ref(false);
const creatingUser = ref(false);
const updatingUser = ref(false);

// Exchange roles options
const exchangeRoles = ref([
  { label: 'Super admin', value: 'super_admin', is_expert: false },
  { label: 'Admin', value: 'admin', is_expert: false },
  { label: 'Member', value: 'member', is_expert: false },
  { label: 'Expert', value: 'member', is_expert: true }
]);

// Form data — legacy (kept for any other ref)
const newUser = ref({
  name: '',
  email: '',
  role: 'member',
  is_expert: false,
  selectedRole: null
});

// Add Person dialog state — mirrors alldoq AddPersonDialog (User vs Associate).
const blankPerson = () => ({
  type: 'system_user',
  name: '',
  email: '',
  discipline: '',
  role: '',
  externally_visible: false
});

const newPerson = ref(blankPerson());

const personRoleOptions = [
  { label: 'Member',      value: 'member' },
  { label: 'Admin',       value: 'admin' },
  { label: 'Super admin', value: 'super_admin' }
];

const roleDescriptions = {
  super_admin: 'Full system access with all administrative privileges',
  admin:       'Administrative access to manage users and settings',
  member:      'Standard user access with limited permissions'
};

const getRoleDescription = (role) => roleDescriptions[role] || 'Select a role to see description';

const openAddPersonDialog = () => {
  newPerson.value = blankPerson();
  newPerson.value.type = 'system_user';
  showAddUserDialog.value = true;
};

const openAddAssociateDialog = () => {
  newPerson.value = blankPerson();
  newPerson.value.type = 'expert';
  showAddUserDialog.value = true;
};

const loadOrgAssociates = async () => {
  if (!organisation.value) return;
  associatesLoading.value = true;
  try {
    const res = await axios.get('/api/v1/associates', {
      params: { organisation_id: organisation.value.id, per_page: 500 }
    });
    associates.value = res.data?.data || [];
  } catch (e) {
    console.error('Associates load error', e);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load associates',
      life: 3000
    });
  } finally {
    associatesLoading.value = false;
  }
};

const editingUser = ref({
  id: null,
  name: '',
  email: '',
  role: 'member',
  is_expert: false,
  selectedRole: null,
  two_factor_method: 'disabled',
  mobile_number: ''
});

// 2FA method options for core (alldoqexchange) users.
// "disabled" maps to two_factor_enabled=false; the others set the method + enabled=true.
const twoFactorMethods = ref([
  { label: 'Disabled', value: 'disabled' },
  { label: 'Email', value: 'email' },
  { label: 'Authenticator app (TOTP)', value: 'totp' },
  { label: 'SMS', value: 'sms' }
]);

// Menu refs
const userMenu = ref();

// Current user for menu actions
const currentMenuUser = ref(null);

// Menu items
const userMenuItems = ref([]);

// Methods
const generatePassword = () => {
  const length = 16;
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
  let password = '';
  for (let i = 0; i < length; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return password;
};

const loadOrganisation = async () => {
  loading.value = true;
  try {
    const response = await axios.get(`/api/v1/organisations/${route.params.id}`);
    organisation.value = response.data.data;

    // Load users once we have the organisation
    await loadUsers();
  } catch (error) {
    console.error('Error loading organisation:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load organisation',
      life: 3000
    });
  } finally {
    loading.value = false;
  }
};

const loadUsers = async () => {
  if (!organisation.value) {
    console.warn('Organisation not loaded yet, skipping users load');
    return;
  }

  usersLoading.value = true;
  try {
    console.log('Loading users for organisation:', route.params.id);

    const response = await axios.get(`/api/v1/organisations/${route.params.id}/users`);
    console.log('Users API response:', response.data);

    // Handle both response.data.data and response.data formats
    users.value = response.data.data || response.data || [];
    console.log('Loaded users count:', users.value.length);
  } catch (error) {
    console.error('Error loading users:', error);
    console.error('Error response:', error.response?.data);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.response?.data?.error || 'Failed to load users',
      life: 3000
    });
  } finally {
    usersLoading.value = false;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Helper functions for role display
const getUserRoleLabel = (user) => {
  if (user.is_expert) return 'Expert';
  switch (user.role) {
    case 'super_admin': return 'Super admin';
    case 'admin': return 'Admin';
    case 'member': return 'Member';
    default: return user.role || 'Member';
  }
};

const getUserRoleSeverity = (user) => {
  if (user.is_expert) return 'warning';
  switch (user.role) {
    case 'super_admin': return 'danger';
    case 'admin': return 'info';
    case 'member': return 'secondary';
    default: return 'secondary';
  }
};

// Role change handlers
const onNewUserRoleChange = (event) => {
  if (event.value) {
    newUser.value.role = event.value.value;
    newUser.value.is_expert = event.value.is_expert;
  }
};

const onEditUserRoleChange = (event) => {
  if (event.value) {
    editingUser.value.role = event.value.value;
    editingUser.value.is_expert = event.value.is_expert;
  }
};

const createPerson = async () => {
  const p = newPerson.value;

  if (!p.name?.trim()) {
    toast.add({ severity: 'warn', summary: 'Name required', life: 2500 });
    return;
  }
  if (!p.email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.email)) {
    toast.add({ severity: 'warn', summary: 'Valid email required', life: 2500 });
    return;
  }
  if (p.type === 'expert' && !p.discipline?.trim()) {
    toast.add({ severity: 'warn', summary: 'Discipline required', life: 2500 });
    return;
  }
  if (p.type === 'system_user' && !p.role) {
    toast.add({ severity: 'warn', summary: 'Role required', life: 2500 });
    return;
  }

  creatingUser.value = true;
  try {
    const userData = {
      name: p.name.trim(),
      email: p.email.trim().toLowerCase(),
      password: generatePassword()
    };

    if (p.type === 'expert') {
      userData.role = 'member';
      userData.is_expert = true;
      userData.discipline = p.discipline.trim();
      userData.active = true;
    } else {
      userData.role = p.role;
      userData.is_expert = false;
      userData.externally_visible = !!p.externally_visible;
    }

    await axios.post(
      `/api/v1/organisations/${route.params.id}/users`,
      { user: userData }
    );

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: p.type === 'expert' ? 'Associate invited successfully' : 'User created successfully',
      life: 3000
    });

    showAddUserDialog.value = false;
    newPerson.value = blankPerson();
    await loadUsers();
    if (p.type === 'expert') await loadOrgAssociates();
  } catch (error) {
    console.error('Error creating person:', error.response?.data, error);
    const data = error.response?.data;
    const detail =
      data?.details?.message ||
      data?.error ||
      data?.message ||
      JSON.stringify(data || {}).slice(0, 200) ||
      'Failed to create person';
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail,
      life: 5000
    });
  } finally {
    creatingUser.value = false;
  }
};

// User actions menu
const toggleUserMenu = (event, user) => {
  currentMenuUser.value = user;
  userMenuItems.value = [
    {
      label: 'Edit User',
      icon: 'pi pi-pencil',
      command: () => handleEditUser(currentMenuUser.value)
    },
    {
      label: 'Resend Confirmation',
      icon: 'pi pi-envelope',
      command: () => confirmResendInvitation(currentMenuUser.value)
    },
    {
      label: 'Set Custom Password',
      icon: 'pi pi-key',
      command: () => openSetPasswordDialog(currentMenuUser.value)
    },
    {
      label: 'Send Terms & Policies',
      icon: 'pi pi-file',
      command: () => confirmSendPolicies(currentMenuUser.value)
    },
    {
      label: 'Copy Portal Link',
      icon: 'pi pi-copy',
      command: () => copyPortalLink()
    },
    {
      label: 'Delete User',
      icon: 'pi pi-trash',
      className: 'p-error',
      command: () => confirmDeleteUser(currentMenuUser.value)
    }
  ];
  userMenu.value.toggle(event);
};

const handleEditUser = (user) => {
  // Find the matching role option based on user's current role and is_expert
  const matchingRole = exchangeRoles.value.find(r =>
    r.value === user.role && r.is_expert === (user.is_expert || false)
  ) || exchangeRoles.value.find(r => r.value === 'member' && !r.is_expert);

  editingUser.value = {
    id: user.id,
    name: user.name,
    email: user.email,
    role: user.role || 'member',
    is_expert: user.is_expert || false,
    selectedRole: matchingRole,
    two_factor_method: user.two_factor_enabled ? (user.two_factor_method || 'email') : 'disabled',
    mobile_number: user.mobile_number || ''
  };
  showEditUserDialog.value = true;
};

const updateUser = async () => {
  try {
    const method = editingUser.value.two_factor_method;
    const mobile = (editingUser.value.mobile_number || '').trim();

    // SMS 2FA needs a deliverable number, otherwise the user is locked out at login.
    if (method === 'sms' && !mobile) {
      toast.add({
        severity: 'warn',
        summary: 'Mobile number required',
        detail: 'A mobile number is required for SMS two-factor authentication.',
        life: 4000
      });
      return;
    }
    if (mobile && !/^\+[1-9]\d{6,14}$/.test(mobile.replace(/[\s\-()]/g, ''))) {
      toast.add({
        severity: 'warn',
        summary: 'Invalid mobile number',
        detail: 'Enter a valid international number starting with + (e.g. +447777777777).',
        life: 4000
      });
      return;
    }

    updatingUser.value = true;

    const userPayload = {
      name: editingUser.value.name,
      email: editingUser.value.email,
      role: editingUser.value.role,
      is_expert: editingUser.value.is_expert
    };

    if (method === 'disabled') {
      userPayload.two_factor_enabled = false;
    } else {
      userPayload.two_factor_enabled = true;
      userPayload.two_factor_method = method;
    }
    // Always send the mobile number when provided so SMS codes can be delivered.
    if (mobile) {
      userPayload.mobile_number = mobile;
    }

    const response = await axios.patch(
      `/api/v1/organisations/${route.params.id}/users/${editingUser.value.id}`,
      { user: userPayload }
    );

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'User updated successfully',
      life: 3000
    });

    showEditUserDialog.value = false;
    await loadUsers();
  } catch (error) {
    console.error('Error updating user:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.response?.data?.error || 'Failed to update user',
      life: 3000
    });
  } finally {
    updatingUser.value = false;
  }
};

const confirmDeleteUser = (user) => {
  confirm.require({
    message: `Are you sure you want to delete ${user.name}?`,
    header: 'Delete Confirmation',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: () => deleteUser(user),
    reject: () => {}
  });
};

const deleteUser = async (user) => {
  try {
    await axios.delete(`/api/v1/organisations/${route.params.id}/users/${user.id}`);

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'User deleted successfully',
      life: 3000
    });

    await loadUsers();
  } catch (error) {
    console.error('Error deleting user:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.response?.data?.error || 'Failed to delete user',
      life: 3000
    });
  }
};

// --- Resend invitation ---
const confirmResendInvitation = (user) => {
  confirm.require({
    message: `Resend invitation email to ${user.email}? A new password will be generated.`,
    header: 'Resend Confirmation',
    icon: 'pi pi-envelope',
    accept: () => resendInvitation(user),
    reject: () => {}
  });
};

const resendInvitation = async (user) => {
  try {
    await axios.post(
      `/api/v1/organisations/${route.params.id}/users/${user.id}/resend_invitation`
    );
    toast.add({
      severity: 'success',
      summary: 'Sent',
      detail: `Invitation email sent to ${user.email}`,
      life: 3000
    });
    await loadUsers();
  } catch (error) {
    console.error('Error resending invitation:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.response?.data?.error || 'Failed to resend invitation',
      life: 4000
    });
  }
};

// --- Send terms & policies (per user, via email) ---
// The documents are managed in "Policies & docs" against the ALLDOQ Core app;
// we resolve its id lazily and email the recipient the current published set.
const coreAppId = ref(null);

const confirmSendPolicies = (user) => {
  confirm.require({
    message: `Email the current ALLDOQ Core terms & policies to ${user.email}?`,
    header: 'Send Terms & Policies',
    icon: 'pi pi-file',
    accept: () => sendPolicies(user),
    reject: () => {}
  });
};

const sendPolicies = async (user) => {
  try {
    if (!coreAppId.value) {
      const { data } = await axios.get('/api/v1/apps');
      const core = (data.apps || []).find(a => a.slug === 'alldoq-core') || (data.apps || [])[0];
      coreAppId.value = core?.id;
    }
    if (!coreAppId.value) {
      toast.add({ severity: 'warn', summary: 'No app configured',
        detail: 'Add the ALLDOQ Core app in "Policies & docs" first.', life: 4000 });
      return;
    }
    const { data } = await axios.post(
      `/api/v1/apps/${coreAppId.value}/email_documents`,
      { email: user.email, name: user.name }
    );
    toast.add({ severity: 'success', summary: 'Sent',
      detail: `Terms & policies emailed to ${user.email} (${data.count} document${data.count === 1 ? '' : 's'})`,
      life: 3500 });
  } catch (error) {
    console.error('Error sending policies:', error);
    const data = error.response?.data || {};
    // No documents published yet → inform clearly rather than a generic error.
    const isNoDocs = data.error === 'no_documents';
    toast.add({
      severity: isNoDocs ? 'warn' : 'error',
      summary: isNoDocs ? 'Nothing to send' : 'Error',
      detail: data.message || data.error || 'Failed to send terms & policies',
      life: 6000
    });
  }
};

// --- Copy portal link (to share via any channel, not just email) ---
// The portal is app-wide; this is the ALLDOQ Core section, so it points at
// the ALLDOQ Core portal. Same link for every ALLDOQ Core client.
const PORTAL_SLUG = 'alldoq-core';

const copyPortalLink = async () => {
  const url = `${window.location.origin}/portal/${PORTAL_SLUG}`;
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(url);
    } else {
      // Fallback for non-secure contexts / older browsers.
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    toast.add({ severity: 'success', summary: 'Copied',
      detail: `Portal link copied: ${url}`, life: 4000 });
  } catch (e) {
    // Last resort: show the link so it can be copied manually.
    toast.add({ severity: 'warn', summary: 'Copy failed',
      detail: `Copy this link manually: ${url}`, life: 8000 });
  }
};

// --- Set custom password ---
const showSetPasswordDialog = ref(false);
const passwordTargetUser = ref(null);
const customPassword = ref('');
const customPasswordConfirm = ref('');
const forceConfirmOnPasswordSet = ref(true);
const settingPassword = ref(false);

const openSetPasswordDialog = (user) => {
  passwordTargetUser.value = user;
  customPassword.value = generateSuggestedPassword();
  customPasswordConfirm.value = customPassword.value;
  forceConfirmOnPasswordSet.value = user.invitation_status !== 'accepted';
  showSetPasswordDialog.value = true;
};

const generateSuggestedPassword = () => {
  const charset = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let pw = '';
  for (let i = 0; i < 14; i++) {
    pw += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return pw + 'A1!';
};

const submitSetPassword = async () => {
  if (customPassword.value.length < 8) {
    toast.add({ severity: 'warn', summary: 'Too short', detail: 'Min 8 characters', life: 3000 });
    return;
  }
  if (customPassword.value !== customPasswordConfirm.value) {
    toast.add({ severity: 'warn', summary: 'Mismatch', detail: 'Passwords do not match', life: 3000 });
    return;
  }
  try {
    settingPassword.value = true;
    await axios.post(
      `/api/v1/organisations/${route.params.id}/users/${passwordTargetUser.value.id}/set_password`,
      {
        password: customPassword.value,
        force_confirm: forceConfirmOnPasswordSet.value
      }
    );
    toast.add({
      severity: 'success',
      summary: 'Updated',
      detail: 'Password set successfully',
      life: 3000
    });
    showSetPasswordDialog.value = false;
    customPassword.value = '';
    customPasswordConfirm.value = '';
    await loadUsers();
  } catch (error) {
    console.error('Error setting password:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.response?.data?.error || 'Failed to set password',
      life: 4000
    });
  } finally {
    settingPassword.value = false;
  }
};

const copyPasswordToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(customPassword.value);
    toast.add({ severity: 'info', summary: 'Copied', detail: 'Password copied', life: 2000 });
  } catch (e) {
    // clipboard API unavailable — silently ignore
  }
};

// --- Invitation status formatting ---
const formatInvitationStatus = (status) => {
  if (!status) return 'N/A';
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const invitationSeverity = (status) => {
  switch (status) {
    case 'accepted': return 'success';
    case 'pending':  return 'warning';
    case 'expired':  return 'danger';
    default:         return 'info';
  }
};

// --- Cases tab ---
const casesStats = ref(null);
const casesStatsLoading = ref(false);
const downloadingCsv = ref(false);

const loadCasesStats = async () => {
  if (casesStats.value) return; // cache first load
  casesStatsLoading.value = true;
  try {
    const res = await axios.get(`/api/v1/organisations/${route.params.id}/cases_stats`);
    casesStats.value = res.data;
  } catch (e) {
    console.error('Cases stats error', e);
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to load case stats', life: 3000 });
  } finally {
    casesStatsLoading.value = false;
  }
};

const downloadCasesCsv = async () => {
  try {
    downloadingCsv.value = true;
    const res = await axios.get(
      `/api/v1/organisations/${route.params.id}/cases_export`,
      {
        responseType: 'blob',
        headers: { Accept: 'text/csv, application/json' }
      }
    );

    const contentType = res.headers['content-type'] || '';
    if (!contentType.includes('csv')) {
      const text = await res.data.text();
      let detail = 'CSV export failed';
      try {
        const parsed = JSON.parse(text);
        detail = parsed.error || parsed.message || detail;
      } catch (_) {
        if (text) detail = text.slice(0, 200);
      }
      toast.add({ severity: 'error', summary: 'Error', detail, life: 5000 });
      return;
    }

    const blob = new Blob([res.data], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cases-${route.params.id}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    console.error('CSV export error', e);
    toast.add({ severity: 'error', summary: 'Error', detail: 'CSV export failed', life: 3000 });
  } finally {
    downloadingCsv.value = false;
  }
};

const formatBytes = (bytes) => {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let n = Number(bytes);
  let i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
};

// --- Feature flags tab ---
// Flag definitions (keys, labels, sections, settings-nesting) come from the
// backend so this view never drifts from the canonical list.
const featureFlagDefs = ref([]);
const featureFlagSections = ref([]);

// Definitions grouped under their section header, preserving section order.
const featureFlagGroups = computed(() =>
  featureFlagSections.value
    .map((name) => ({
      name,
      flags: featureFlagDefs.value.filter((f) => f.section === name)
    }))
    .filter((group) => group.flags.length)
);

const isNestedFlag = (flag) => !!flag.nested;

const loadFeatureFlagDefs = async () => {
  if (featureFlagDefs.value.length) return;
  try {
    const res = await axios.get('/api/v1/feature_flags/definitions');
    featureFlagDefs.value = res.data?.flags || [];
    featureFlagSections.value = res.data?.sections || [];
  } catch (e) {
    // Non-fatal: the tab just renders empty if definitions fail to load.
  }
};

const orgFlagsLoading = ref(false);
const savingFlag = ref(null);

const getOrgFlag = (flag) => {
  if (!organisation.value) return false;
  if (isNestedFlag(flag)) {
    return !!organisation.value.settings?.[flag.key];
  }
  return !!organisation.value[flag.key];
};

const applyOrgFlag = async (flag, value) => {
  const previous = getOrgFlag(flag);
  // Optimistic update
  if (isNestedFlag(flag)) {
    organisation.value.settings = organisation.value.settings || {};
    organisation.value.settings[flag.key] = value;
  } else {
    organisation.value[flag.key] = value;
  }
  savingFlag.value = flag.key;
  try {
    await axios.patch(`/api/v1/organisations/${route.params.id}/features`, {
      features: { [flag.key]: value }
    });
    if (flag.key === 'is_organisation') {
      // The backend applied the full entity-type default profile; reload to
      // reflect every flag it reset, not just this toggle.
      await loadOrganisation();
      toast.add({
        severity: 'success',
        summary: 'Defaults applied',
        detail: `${value ? 'Organisation' : 'Expert'} defaults applied`,
        life: 2500
      });
    } else {
      toast.add({
        severity: 'success',
        summary: 'Updated',
        detail: `${flag.label}: ${value ? 'on' : 'off'}`,
        life: 2000
      });
    }
  } catch (e) {
    if (isNestedFlag(flag)) {
      organisation.value.settings[flag.key] = previous;
    } else {
      organisation.value[flag.key] = previous;
    }
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: e.response?.data?.error || 'Failed to update flag',
      life: 3000
    });
  } finally {
    savingFlag.value = null;
  }
};

const updateOrgFlag = (flag, value) => {
  // Changing the entity type is a full reset of every flag to that type's
  // defaults, so confirm before applying.
  if (flag.key === 'is_organisation') {
    confirm.require({
      message: `Set this organisation to ${value ? 'organisation (firm)' : 'expert'}? `
        + `This resets all feature flags to the ${value ? 'organisation' : 'expert'} defaults.`,
      header: 'Apply entity-type defaults',
      icon: 'pi pi-exclamation-triangle',
      accept: () => applyOrgFlag(flag, value),
      reject: () => {}
    });
    return;
  }
  applyOrgFlag(flag, value);
};

watch(activeTab, (idx) => {
  // Index map: Users=0, Associates=1, Cases=2, Feature flags=3, Settings=4.
  if (idx === 1) loadOrgAssociates();
  if (idx === 2) loadCasesStats();
});

// Lifecycle
onMounted(async () => {
  if (authStore.isAuthenticated) {
    loadFeatureFlagDefs();
    await loadOrganisation();
  } else {
    router.push({ name: 'Login' });
  }
});

// Watch for organisation ID changes
watch(
  () => route.params.id,
  (newId) => {
    if (newId) {
      loadOrganisation();
    }
  }
);

// Reload when the exchange instance (core/tula) changes so the toggle takes
// effect without a full page reload. The current org id may not exist in the
// other instance, so a fresh load shows that instance's not-found state.
watch(() => authStore.exchangeInstance, () => {
  activeTab.value = 0;
  loadFeatureFlagDefs();
  loadOrganisation();
});
</script>

<style scoped>
:deep(.p-tabview .p-tabview-panels) {
  padding: 1rem 0;
}

:deep(.p-datatable-tbody > tr > td) {
  padding: 0.75rem 1rem;
}

/* Dialog styling */
:deep(.add-user-dialog .p-dialog-header) {
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

:deep(.add-user-dialog .p-dialog-content) {
  padding: 0;
}

:deep(.add-user-dialog .p-dialog-footer) {
  padding: 0;
  border-top: 1px solid #e5e7eb;
}

.p-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.p-inputtext {
  border: 1px solid #ced4da !important;
  border-radius: 4px !important;
  transition: border-color 0.2s, box-shadow 0.2s !important;
  width: 100% !important;
  display: block !important;
}

.p-inputtext:hover {
  border-color: #2563eb !important;
}

.p-inputtext:focus {
  border-color: #2563eb !important;
  box-shadow: 0 0 0 0.2rem rgba(37, 99, 235, 0.25) !important;
  outline: 0 none !important;
}

.field {
  margin-bottom: 1rem;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
}

/* Menu styling */
:deep(.p-menu) {
  width: 200px;
}

:deep(.p-menu .p-menuitem-link) {
  padding: 0.75rem 1rem;
}

:deep(.p-menu .p-menuitem-link .p-menuitem-icon) {
  margin-right: 0.75rem;
}

:deep(.p-menu .p-menuitem-link .p-menuitem-text) {
  font-size: 0.9rem;
}

:deep(.p-error .p-menuitem-text, .p-error .p-menuitem-icon) {
  color: #ef4444;
}
</style>
